import StudentAvatar from './StudentAvatar';
import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, Drawer, List, ListItem,
  ListItemText, CssBaseline, Box, Divider, IconButton,
  Avatar, Stack, ListSubheader, useTheme, Dialog,
  DialogTitle, DialogContent, Button, TextField, Alert,
  Stepper, Step, StepLabel, Card, CardContent, Grid,
  InputAdornment, Chip, CircularProgress,
  Menu, MenuItem, Tooltip, Badge, useMediaQuery
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  School as SchoolIcon,
  Class as ClassIcon,
  Assignment as AssignmentIcon,
  Grade as GradeIcon,
  Event as EventIcon,
  Notifications as NotificationsIcon,
  PersonAdd as PersonAddIcon,
  Campaign as CampaignIcon,
  Book as BookIcon,
  ExitToApp as ExitToAppIcon,
  Groups as GroupsIcon,
  Payment as PaymentIcon,
  Info as InfoIcon,
  AttachMoney as AttachMoneyIcon,
  LocationOn as LocationOnIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  DirectionsBus as BusIcon,
  Settings as SettingsIcon,
  PhotoCamera as PhotoCameraIcon,
  Edit as EditIcon,
  Menu as MenuIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Inventory as InventoryIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Search as SearchIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { styled, alpha } from '@mui/material/styles';
import api from '../utils/Api';
import { subscribeUserToPush } from '../utils/pushNotifications';
import socket from '../utils/socket';
import { ColorModeContext } from '../App';
import { hasPermission } from '../utils/permissions';
import WhatsNewDialog from './WhatsNewDialog';
import InternalWorkspace from './InternalWorkspace';

const drawerWidth = 300;
const compactDrawerWidth = 260;
const mobileDrawerWidth = 280;
const mobileTopBarHeight = 72;
const desktopTopBarHeight = 80;
const sidebarBg = '#0f172a';
const sidebarBgSoft = '#111c2f';
const sidebarBorder = 'rgba(148, 163, 184, 0.18)';
const sidebarText = '#e5e7eb';
const sidebarMuted = '#94a3b8';
const sidebarActive = '#60a5fa';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  boxSizing: 'border-box',
  borderBottom: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.55 : 0.7)}`,
  boxShadow: 'none',
  backgroundImage: 'none',
  paddingTop: 'var(--safe-area-top)',
  paddingLeft: 'var(--safe-area-left)',
  paddingRight: 'var(--safe-area-right)',
  [theme.breakpoints.down('md')]: {
    borderBottom: 0,
  },
}));

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  [theme.breakpoints.between('md', 'xl')]: {
    width: compactDrawerWidth,
  },
  '& .MuiDrawer-paper': {
    boxSizing: 'border-box',
    width: drawerWidth,
    backgroundColor: sidebarBg,
    backgroundImage: `linear-gradient(180deg, ${sidebarBgSoft} 0%, ${sidebarBg} 46%, #0b1120 100%)`,
    color: sidebarText,
    borderRight: `1px solid ${sidebarBorder}`,
    boxShadow: '10px 0 30px rgba(15, 23, 42, 0.18)',
    paddingTop: 'var(--safe-area-top)',
    paddingBottom: 'var(--safe-area-bottom)',
    paddingLeft: 'var(--safe-area-left)',
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.standard,
    }),
    [theme.breakpoints.between('md', 'xl')]: {
      width: compactDrawerWidth,
    },
    [theme.breakpoints.down('md')]: {
      width: mobileDrawerWidth,
    },
    '& .MuiListSubheader-root': {
      color: `${sidebarMuted} !important`,
      backgroundColor: 'transparent !important',
    },
    '& .MuiDivider-root': {
      borderColor: sidebarBorder,
    },
  },
}));

const StyledListItem = styled(ListItem)(({ selected }) => ({
  borderRadius: 8,
  margin: '4px 12px',
  padding: '12px 16px',
  transition: 'border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease',
  border: selected
    ? `1px solid #ffffff`
    : `1px solid transparent`,
  background: selected
    ? '#ffffff'
    : 'transparent',
  boxShadow: 'none',
  '&:hover': {
    background: selected ? '#ffffff' : alpha('#ffffff', 0.1),
    borderColor: selected ? '#ffffff' : alpha('#ffffff', 0.28),
    boxShadow: 'none',
  },
  '& .MuiListItemText-primary': {
    fontWeight: selected ? 700 : 500,
    color: selected ? '#000000' : sidebarText,
    fontSize: '0.9rem',
    transition: 'all 0.2s ease-in-out',
  },
  '& .MuiSvgIcon-root': {
    color: selected ? '#000000' : sidebarMuted,
    fontSize: '1.2rem',
    transition: 'all 0.2s ease-in-out',
  },
  '&:hover .MuiListItemText-primary': {
    color: selected ? '#000000' : '#ffffff',
  },
  '&:hover .MuiSvgIcon-root': {
    color: selected ? '#000000' : '#ffffff',
  },
  width: 'calc(100% - 24px)',
  minHeight: 52,
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
}));

const MainContent = styled('main')(({ theme }) => ({
  flexGrow: 1,
  boxSizing: 'border-box',
  minHeight: '100dvh',
  padding: theme.spacing(3),
  paddingBottom: `calc(${theme.spacing(3)} + var(--safe-area-bottom))`,
  paddingLeft: `calc(${theme.spacing(3)} + var(--safe-area-left))`,
  paddingRight: `calc(${theme.spacing(3)} + var(--safe-area-right))`,
  marginLeft: 0,
  width: `calc(100% - ${drawerWidth}px)`,
  overflowX: 'hidden',
  minWidth: 0,
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.standard,
  }),
  [theme.breakpoints.between('md', 'xl')]: {
    width: `calc(100% - ${compactDrawerWidth}px)`,
    padding: theme.spacing(2.25),
    paddingBottom: `calc(${theme.spacing(2.25)} + var(--safe-area-bottom))`,
    paddingLeft: `calc(${theme.spacing(2.25)} + var(--safe-area-left))`,
    paddingRight: `calc(${theme.spacing(2.25)} + var(--safe-area-right))`,
  },
  [theme.breakpoints.down('md')]: {
    width: '100%',
    marginLeft: 0,
    padding: theme.spacing(2),
    paddingBottom: `calc(${theme.spacing(2)} + var(--safe-area-bottom))`,
    paddingLeft: `calc(${theme.spacing(2)} + var(--safe-area-left))`,
    paddingRight: `calc(${theme.spacing(2)} + var(--safe-area-right))`,
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
    paddingBottom: `calc(${theme.spacing(1)} + var(--safe-area-bottom))`,
    paddingLeft: `calc(${theme.spacing(1)} + var(--safe-area-left))`,
    paddingRight: `calc(${theme.spacing(1)} + var(--safe-area-right))`,
  },
}));

const AppBarOffset = styled('div')(({ theme }) => ({
  minHeight: `calc(${mobileTopBarHeight}px + var(--safe-area-top))`,
  [theme.breakpoints.up('sm')]: {
    minHeight: `calc(${desktopTopBarHeight}px + var(--safe-area-top))`,
  },
}));

const SectionContainer = styled(Box)(() => ({
  padding: '8px 0',
}));

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      animation: 'ripple 1.2s infinite ease-in-out',
      border: '1px solid currentColor',
      content: '""',
    },
  },
  '@keyframes ripple': {
    '0%': {
      transform: 'scale(.8)',
      opacity: 1,
    },
    '100%': {
      transform: 'scale(2.4)',
      opacity: 0,
    },
  },
}));

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

export default function DashboardLayout() {
  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [storedUser, setStoredUser] = useState(() =>
    JSON.parse(localStorage.getItem('user') || '{}')
  );

  const userName = storedUser.name || 'Admin';
  const userInitial = userName.charAt(0).toUpperCase();
  const schoolName = storedUser.school?.name || 'School Admin';
  const isAdmin = storedUser.role === 'admin' || storedUser.role === 'superadmin';

  // Media queries for responsive design
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const isCompactDesktop = useMediaQuery(theme.breakpoints.between('md', 'xl'));
  const activeDrawerWidth = isCompactDesktop ? compactDrawerWidth : drawerWidth;

  // State for user menu and profile picture
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [profilePicture, setProfilePicture] = useState(storedUser.profilePicture || '');
  const [isUploading, setIsUploading] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // ✅ Topbar Global Actions State (Moved from HomePage)
  const [search, setSearch] = useState('');
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [loadingSearch, setLoadingSearch] = useState(false);

  const [notifications, setNotifications] = useState([]);
  const [anchorElNotifications, setAnchorElNotifications] = useState(null);
  const [notificationsLoaded, setNotificationsLoaded] = useState(false);

  // Search Handler
  const handleSearch = useCallback(async (query) => {
    if (!query) return;
    setLoadingSearch(true);
    try {
      const res = await api.get(`/search?q=${query}`);
      setSearchResults(res.data.data);
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setLoadingSearch(false);
    }
  }, []);

  const handleNotificationClick = (note) => {
    setAnchorElNotifications(null);

    switch (note.type) {
      case 'announcement':
        navigate(`/dashboard/announcements`);
        break;
      case 'reset-request':
      case 'reset-approved':
      case 'reset-rejected':
        navigate(`/dashboard/reset-requests`);
        break;
      case 'attendance':
        navigate(`/dashboard/attendance`);
        break;
      case 'teacher-attendance':
        navigate(`/dashboard/teacher/attendance`);
        break;
      case 'assignment':
        navigate(`/dashboard/assignments`);
        break;
      case 'feedingfee':
        navigate(`/dashboard/feeding-fees`);
        break;
      case 'parent-transport':
        navigate(`/dashboard/transport/dashboard`);
        break;
      case 'fee':
      case 'transaction':
        navigate(`/dashboard/fees`);
        break;
      case 'agenda':
        navigate(`/dashboard/calendar`);
        break;
      case 'exam-report':
      case 'report-card':
        navigate(`/dashboard/grades`);
        break;
      default:
        navigate(`/dashboard/notifications?scrollTo=${note._id}`);
    }
  };

  const handleBellClick = (event) => {
    setAnchorElNotifications(event.currentTarget);
    if (!notificationsLoaded) {
      fetchDesktopNotifications();
    }
  };

  const fetchDesktopNotifications = useCallback(async () => {
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data || []);
      setNotificationsLoaded(true);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  }, []);

  // Filter unread notifications
  const unreadCount = React.useMemo(() => {
    const userId = storedUser._id || storedUser.id;
    if (!userId) return notifications.length;
    return notifications.filter(n => {
      if (Array.isArray(n.readBy)) {
        return !n.readBy.includes(userId);
      }
      return !n.isRead;
    }).length;
  }, [notifications, storedUser]);

  const schoolType = storedUser.school?.schoolType || 'Private';

  const canShowFeeding =
    schoolType === 'Private' &&
    storedUser.school?.features?.feedingEnabled !== false;




  // ✅ Reset Password Modal States
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [isResetting, setIsResetting] = useState(false);

  // School Location Modal State
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [polygonCoords, setPolygonCoords] = useState('');
  const [validationError, setValidationError] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [isValidJSON, setIsValidJSON] = useState(false);
  const [coordinates, setCoordinates] = useState([]);
  const [currentLat, setCurrentLat] = useState('');
  const [currentLng, setCurrentLng] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  // Fetch user profile with useCallback to prevent infinite re-renders
  const fetchUserProfile = useCallback(async () => {
    try {
      const response = await api.get('/profile');
      const userData = response.data;

      // Update local storage with fresh data
      const updatedUser = { ...storedUser, ...userData };
      localStorage.setItem('user', JSON.stringify(updatedUser));

      // Update state
      setProfilePicture(userData.profilePicture || '');
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
    }
  }, [storedUser]); // Add storedUser as dependency

  // Unlock audio play on first user interaction
  useEffect(() => {
    const unlockAudio = () => {
      const audio = new Audio('/notification.mp3');
      audio.muted = true;
      audio.play().then(() => {
        audio.pause();
        audio.currentTime = 0;
      }).catch(() => { /* ignore */ });
      document.body.removeEventListener('click', unlockAudio);
      document.body.removeEventListener('touchstart', unlockAudio);
    };
    document.body.addEventListener('click', unlockAudio);
    document.body.addEventListener('touchstart', unlockAudio);
    return () => {
      document.body.removeEventListener('click', unlockAudio);
      document.body.removeEventListener('touchstart', unlockAudio);
    };
  }, []);

  // Initialize Web Push Notifications & Socket Listener
  useEffect(() => {
    fetchUserProfile();
    fetchDesktopNotifications();
    subscribeUserToPush();

    // Play sound on new socket notification
    const handleNotification = (data) => {
      console.log('App: Notification received via socket', data);

      // Add notification directly to layout state
      setNotifications((prev) => [data, ...prev]);

      try {
        // 1) Trigger system notification (ensures OS sound plays even if audio gets blocked by autoplay policy)
        if ('Notification' in window && Notification.permission === 'granted') {
          try {
            new Notification(data.title || 'New Notification', {
              body: data.message || 'You have a new message',
              icon: '/logo192.png'
            });
          } catch (e) {
            console.warn('System notification failed:', e);
          }
        }

        // 2) Try playing the manual audio file (notification.mp3 only)
        const audio = new Audio('/notification.mp3');
        const playPromise = audio.play();

        if (playPromise !== undefined) {
          playPromise.catch((e) => {
            console.warn('Audio play failed, user must interact with the page first:', e.message);
          });
        }
      } catch (err) {
        console.error('Error playing notification sound:', err);
      }
    };

    socket.on('notification', handleNotification);

    socket.on('connect_error', (err) => {
      console.error('Socket Connection Error:', err.message);
    });

    const registerSocket = () => {
      const userId = storedUser._id || storedUser.id;
      if (userId) {
        socket.emit('register', userId);
      }
    };

    socket.on('connect', () => {
      registerSocket();
    });

    // If already connected when mounting
    if (socket.connected) {
      registerSocket();
    }

    // Listen for manual notification updates from pages (like read/delete)
    const handleLocalNotificationsUpdate = () => {
      fetchDesktopNotifications();
    };
    window.addEventListener('notifications-updated', handleLocalNotificationsUpdate);

    return () => {
      socket.off('notification', handleNotification);
      socket.off('connect_error');
      socket.off('connect');
      window.removeEventListener('notifications-updated', handleLocalNotificationsUpdate);
    };
  }, [fetchUserProfile, fetchDesktopNotifications]);

  const handleDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  // CLEANUP: Remove legacy token if present
  // CLEANUP: Legacy token removal REMOVED to fix API access
  // useEffect(() => {
  //   if (localStorage.getItem('token')) {
  //     localStorage.removeItem('token');
  //   }
  // }, []);

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (e) {
      console.error('Logout failed', e);
    }
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    if (theme.palette.mode === 'dark') {
      colorMode.toggleColorMode();
    }
    navigate('/login');
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleProfilePictureUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('profilePicture', file);

      const response = await api.post('/profile/picture', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Update profile picture in state
      setProfilePicture(response.data.profilePicture);

      // Update user in localStorage
      const updatedUser = { ...storedUser, profilePicture: response.data.profilePicture };
      localStorage.setItem('user', JSON.stringify(updatedUser));

      // Close user menu
      handleUserMenuClose();

      // Show success message
      alert('Profile picture updated successfully!');
    } catch (error) {
      console.error('Failed to upload profile picture:', error);
      alert('Failed to upload profile picture. Please try again.');
    } finally {
      setIsUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleRemoveProfilePicture = async () => {
    if (!window.confirm('Are you sure you want to remove your profile picture?')) {
      return;
    }

    try {
      // If you have a backend endpoint to remove profile picture, call it here
      // For now, we'll just clear it from the frontend
      setProfilePicture('');

      // Update user in localStorage
      const updatedUser = { ...storedUser, profilePicture: '' };
      localStorage.setItem('user', JSON.stringify(updatedUser));

      handleUserMenuClose();
      alert('Profile picture removed successfully!');
    } catch (error) {
      console.error('Failed to remove profile picture:', error);
      alert('Failed to remove profile picture. Please try again.');
    }
  };

  // ✅ Handle Password Reset
  const handleResetPassword = async () => {
    if (!oldPassword || !newPassword || !confirmPassword) {
      setResetMessage('⚠️ Please fill in all fields.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setResetMessage('❌ New passwords do not match.');
      return;
    }

    setIsResetting(true);
    setResetMessage('');

    try {
      const token = localStorage.getItem('token');
      const response = await api.post(
        '/auth/change-password',
        { oldPassword, newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setResetMessage('✅ Password updated successfully!');
      setTimeout(() => {
        setResetModalOpen(false);
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setResetMessage('');
      }, 1500);
    } catch (error) {
      const errMsg =
        error.response?.data?.message || '❌ Failed to reset password. Try again.';
      setResetMessage(errMsg);
    } finally {
      setIsResetting(false);
    }
  };

  const isActiveRoute = (path) => {
    if (path === '/dashboard') {
      return location.pathname === path;
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Navigation items organized by category
  const navigationItems = React.useMemo(() => {
    let items = {
      academic: [
        { path: '/dashboard', icon: <DashboardIcon color="primary" />, text: 'Dashboard Home', permission: 'canViewDashboard' },
        { path: '/dashboard/school-info', icon: <InfoIcon color="primary" />, text: 'School Details' },
        { path: '/dashboard/calendar', icon: <EventIcon color="primary" />, text: 'Academic Calendar' },
        { path: '/dashboard/classes', icon: <ClassIcon color="primary" />, text: 'Manage Classes' },
        { path: '/dashboard/students', icon: <GroupsIcon color="primary" />, text: 'Manage Students' },
        { path: '/dashboard/teachers', icon: <SchoolIcon color="primary" />, text: 'Manage Teachers', permission: 'canViewTeachers' },
        { path: '/dashboard/parents', icon: <PeopleIcon color="primary" />, text: 'Manage Parents' },

        { path: '/dashboard/attendance', icon: <EventIcon color="primary" />, text: 'Student Attendance', hiddenFeatureKeys: ['student_attendance_card'], permission: 'canViewAttendance' },
        { path: '/dashboard/teacher/attendance', icon: <EventIcon color="primary" />, text: 'Teacher Attendance', hiddenFeatureKeys: ['teacher_attendance_card'], permission: 'canViewAttendance' },
        { path: '/dashboard/grades', icon: <GradeIcon color="primary" />, text: 'Exams & Reports', featureKeys: ['exams', 'reports'], hiddenFeatureKeys: ['teacher_exams_report_card'], permission: 'canExportReports' },
        { path: '/dashboard/sba-records', icon: <InventoryIcon color="primary" />, text: 'Exams and Report', featureKeys: ['exams', 'reports'], hiddenFeatureKeys: ['teacher_sba_direct_entry_card'], permission: 'canExportReports' },
        { path: '/dashboard/assignments', icon: <AssignmentIcon color="primary" />, text: 'Assignments', hiddenFeatureKeys: ['teacher_assignments_card'] },
        { path: '/dashboard/exercises', icon: <BookIcon color="primary" />, text: 'Exercise Records', hiddenFeatureKeys: ['teacher_weekly_exercise_card'] },
        { path: '/dashboard/enrollment', icon: <PersonAddIcon color="primary" />, text: 'Enrollment' },
      ],
      operations: [
        { path: '/dashboard/fees', icon: <AttachMoneyIcon color="primary" />, text: 'Fees Management', permission: 'canViewFees' },
        { path: '/dashboard/feeding-fees', icon: <PaymentIcon color="primary" />, text: 'Feeding Fee Management', hiddenFeatureKeys: ['teacher_feeding_fee_card'], permission: 'canViewFeedingFee' },
        { path: '/dashboard/class-fees', icon: <AttachMoneyIcon color="primary" />, text: 'Class Fee Setup', hiddenFeatureKeys: ['teacher_class_fee_card'], permission: 'canEditFees' },
        { path: '/dashboard/expense-profit', icon: <AttachMoneyIcon color="primary" />, text: 'Expenses & Profit', permission: 'canViewFees' },
        { path: '/dashboard/students/promote', icon: <GroupsIcon color="primary" />, text: 'Promote Students' },
        { path: '/dashboard/transport/dashboard', icon: <DashboardIcon color="primary" />, text: 'Transport Analytics' },
        { path: '/dashboard/transport/buses', icon: <BusIcon color="primary" />, text: 'Transport Buses' },
        { path: '/dashboard/transport/routes', icon: <LocationOnIcon color="primary" />, text: 'Transport Routes' },
        { path: '/dashboard/transport/enrollment', icon: <PersonAddIcon color="primary" />, text: 'Transport Enrollment' },
        { path: '/dashboard/transport/class-boarding', icon: <BusIcon color="primary" />, text: 'School Bus Management', hiddenFeatureKeys: ['teacher_transport_card'] },
        { path: '/dashboard/admission-register', icon: <BookIcon color="primary" />, text: 'Admission Register' },
        { path: '/dashboard/transport/assignments', icon: <AssignmentIcon color="primary" />, text: 'Bus Assignments' },
        { path: '/dashboard/transport/reports', icon: <AssignmentIcon color="primary" />, text: 'Transport Reports' },
        { path: '/dashboard/reset-requests', icon: <SettingsIcon color="primary" />, text: 'Password Reset Requests', permission: 'canManageAdmins' },
        { path: '/dashboard/admins', icon: <AdminPanelSettingsIcon color="primary" />, text: 'Admin Management', permission: 'canManageAdmins' },
        { path: '/dashboard/payroll/processing', icon: <PaymentIcon color="primary" />, text: 'Teacher Payroll', featureKey: 'payroll', hiddenFeatureKeys: ['teacher_payslips_card'] },
        { path: '/dashboard/logbook', icon: <BookIcon color="primary" />, text: 'School Logbook' },
      ],
      communication: [
        { path: '/dashboard/announcements', icon: <CampaignIcon color="primary" />, text: 'Announcements', hiddenFeatureKeys: ['teacher_announcements_card'] },
        { path: '/dashboard/sms', icon: <CampaignIcon color="primary" />, text: 'SMS Notifications' },
        { path: '/dashboard/notifications', icon: <NotificationsIcon color="primary" />, text: 'Notifications' },
      ],
      billing: [
        { path: '/dashboard/platform-billing', icon: <PaymentIcon color="primary" />, text: 'CoreVista Billing' },
      ],

    };

    const lockedFeatures = storedUser?.school?.lockedFeatures || [];
    const hideLockedItems = (sectionItems = []) => sectionItems.filter((item) => (
      !item.hiddenFeatureKeys?.some((key) => lockedFeatures.includes(key))
    ));

    const hideUnauthorizedItems = (sectionItems = []) => sectionItems.filter((item) => (
      !item.permission || hasPermission(item.permission, storedUser)
    ));

    Object.keys(items).forEach((sectionKey) => {
      items[sectionKey] = hideUnauthorizedItems(hideLockedItems(items[sectionKey]));
    });

    const sType = (storedUser?.school?.schoolType || 'Private').toLowerCase();

    // Hide transport items from the sidebar except class boarding
    items.operations = items.operations.filter((item) => {
      if (item.path === '/dashboard/transport/class-boarding') {
        return true;
      }

      return !item.path.startsWith('/dashboard/transport/');
    });

    if (sType !== 'private' && sType !== 'private school') {
      items.operations = items.operations.filter(item => item.text !== 'Teacher Payroll');
    }

    if (sType === 'government' || sType === 'basic') {
      items.operations.unshift(
        { path: '/dashboard/school-records', icon: <InventoryIcon color="primary" />, text: 'School Records' }
      );
      items.operations = items.operations.filter(item =>
        !['Fees Management', 'Feeding Fee Management', 'Class Fee Setup', 'Expenses & Profit', 'School Bus Management'].includes(item.text)
      );
    }

    if (storedUser.role === 'superadmin') {
      items.superAdmin = [
        { path: '/dashboard/super-admin/schools', icon: <AdminPanelSettingsIcon color="primary" />, text: 'Manage Schools' }
      ];
    }

    return items;
  }, [storedUser]);

  // Helper to render a single sidebar item with lock check
  const renderSidebarItem = (item) => {
    let isLocked = false;

    // Check if the item has a single featureKey or multiple featureKeys
    if (item.featureKey) {
      isLocked = storedUser.school?.lockedFeatures?.includes(item.featureKey);
    } else if (item.featureKeys && Array.isArray(item.featureKeys)) {
      isLocked = item.featureKeys.some(fk => storedUser.school?.lockedFeatures?.includes(fk));
    }

    if (isLocked) {
      return (
        <Tooltip title="This feature is locked for your school" placement="right" key={item.path}>
          <StyledListItem
            sx={{
              justifyContent: 'initial',
              px: 2,
              minHeight: 48,
              opacity: 0.5,
              cursor: 'not-allowed',
              bgcolor: 'transparent'
            }}
          >
            <Box sx={{
              minWidth: 0,
              mr: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              {item.icon}
            </Box>
            <ListItemText
              primary={item.text}
              sx={{
                '& .MuiListItemText-primary': {
                  fontSize: '0.875rem',
                  whiteSpace: 'nowrap',
                }
              }}
            />
            <LockIcon fontSize="small" sx={{ ml: 'auto', color: sidebarMuted }} />
          </StyledListItem>
        </Tooltip>
      );
    }

    return (
      <StyledListItem
        key={item.path}
        component={Link}
        to={item.path}
        selected={isActiveRoute(item.path)}
        onClick={() => isMobile && setMobileDrawerOpen(false)}
        sx={{
          justifyContent: 'initial',
          px: 2,
          minHeight: 48,
        }}
      >
        <Box sx={{
          minWidth: 0,
          mr: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          {item.icon}
        </Box>
        <ListItemText
          primary={item.text}
          sx={{
            '& .MuiListItemText-primary': {
              fontSize: '0.875rem',
              whiteSpace: 'nowrap',
            }
          }}
        />
      </StyledListItem>
    );
  };

  const addCoordinate = () => {
    if (!currentLat || !currentLng) {
      setValidationError('Please enter both latitude and longitude');
      return;
    }

    const lat = parseFloat(currentLat);
    const lng = parseFloat(currentLng);

    if (isNaN(lat) || isNaN(lng)) {
      setValidationError('Please enter valid numbers for coordinates');
      return;
    }

    if (lat < -90 || lat > 90) {
      setValidationError('Latitude must be between -90 and 90');
      return;
    }

    if (lng < -180 || lng > 180) {
      setValidationError('Longitude must be between -180 and 180');
      return;
    }

    setCoordinates([...coordinates, [lng, lat]]);
    setCurrentLat('');
    setCurrentLng('');
    setValidationError('');
    generateJSON([...coordinates, [lng, lat]]);
  };

  const removeCoordinate = (index) => {
    const newCoordinates = coordinates.filter((_, i) => i !== index);
    setCoordinates(newCoordinates);
    generateJSON(newCoordinates);
  };

  const generateJSON = (coords) => {
    if (coords.length === 0) {
      setPolygonCoords('');
      setIsValidJSON(false);
      return;
    }

    let polygon = coords;
    if (coords.length > 2) {
      const first = coords[0];
      const last = coords[coords.length - 1];
      if (first[0] !== last[0] || first[1] !== last[1]) {
        polygon = [...coords, first];
      }
    }

    const jsonString = JSON.stringify([polygon], null, 2);
    setPolygonCoords(jsonString);
    validateJSON(jsonString);
  };

  const validateJSON = (jsonString) => {
    try {
      if (!jsonString.trim()) {
        setValidationError('Please enter coordinates');
        setIsValidJSON(false);
        return false;
      }

      const parsed = JSON.parse(jsonString);

      if (!Array.isArray(parsed) || !parsed.every(Array.isArray)) {
        setValidationError('Coordinates must be an array of arrays');
        setIsValidJSON(false);
        return false;
      }

      const firstArray = parsed[0];
      if (firstArray.length > 0) {
        const firstPoint = JSON.stringify(firstArray[0]);
        const lastPoint = JSON.stringify(firstArray[firstArray.length - 1]);

        if (firstPoint !== lastPoint) {
          setValidationError('First and last coordinates must match to form a closed polygon');
          setIsValidJSON(false);
          return false;
        }
      }

      setValidationError('');
      setIsValidJSON(true);
      return true;
    } catch (error) {
      setValidationError(`Invalid JSON format: ${error.message}`);
      setIsValidJSON(false);
      return false;
    }
  };

  const handleCoordinatesChange = (e) => {
    const value = e.target.value;
    setPolygonCoords(value);
    validateJSON(value);
  };

  const handleNext = () => {
    if (activeStep === 0 && validateJSON(polygonCoords)) {
      setActiveStep(1);
    }
  };

  const handleBack = () => {
    setActiveStep(0);
  };

  const validateJSONFormat = (jsonString) => {
    try {
      JSON.parse(jsonString);
      return true;
    } catch (e) {
      return false;
    }
  };

  const normalizeGeoJSONCoordinates = (coords) => {
    try {
      if (Array.isArray(coords) &&
        Array.isArray(coords[0]) &&
        Array.isArray(coords[0][0]) &&
        typeof coords[0][0][0] === 'number') {
        return coords;
      }

      if (Array.isArray(coords) &&
        Array.isArray(coords[0]) &&
        typeof coords[0][0] === 'number') {
        return [coords];
      }

      if (Array.isArray(coords) &&
        typeof coords[0] === 'number' &&
        coords.length === 2) {
        return [[coords]];
      }

      return null;
    } catch (e) {
      console.error('Error normalizing coordinates:', e);
      return null;
    }
  };

  const handleUpdateLocation = async () => {
    if (!validateJSONFormat(polygonCoords)) {
      alert('❌ Invalid JSON format. Please check your coordinates.');
      return;
    }

    let coordsArray;
    try {
      coordsArray = JSON.parse(polygonCoords);

      if (!Array.isArray(coordsArray) || coordsArray.length === 0) {
        alert('❌ Coordinates must be a non-empty array');
        return;
      }

      coordsArray = normalizeGeoJSONCoordinates(coordsArray);

      if (!coordsArray) {
        alert('❌ Coordinates must be in GeoJSON Polygon format: [[[lng, lat], [lng, lat], ...]]');
        return;
      }

      const ring = coordsArray[0];
      const firstPoint = ring[0];
      const lastPoint = ring[ring.length - 1];

      if (firstPoint[0] !== lastPoint[0] || firstPoint[1] !== lastPoint[1]) {
        if (!window.confirm('⚠️ Polygon does not appear to be closed (first and last points differ). Do you want to continue anyway?')) {
          return;
        }
      }

      if (ring.length < 4) {
        alert('❌ Polygon must have at least 4 points (including the closing point)');
        return;
      }
    } catch (err) {
      alert('❌ Failed to parse coordinates: ' + err.message);
      return;
    }

    setIsUpdating(true);

    try {
      const schoolId = storedUser.school?.id;
      const token = localStorage.getItem('token');

      if (!schoolId || !token) {
        alert('❌ Missing school info or token. Please log in again.');
        handleLogout();
        return;
      }

      const response = await api.put(`/school-info/${schoolId}/location`, {
        coordinates: coordsArray,
      });

      alert(`✅ School location updated successfully! ${response.data.message || ''}`);
      setLocationModalOpen(false);
      setActiveStep(0);
      setPolygonCoords('');
      setCoordinates([]);

      fetchSchoolData();
    } catch (err) {
      console.error('❌ Update location failed:', err);

      let errorMessage = 'Failed to update location';
      if (err.response) {
        errorMessage = `❌ ${err.response.status} - ${err.response.data.message || err.response.statusText}`;

        if (err.response.status === 400 && err.response.data.errors) {
          errorMessage += '\n' + err.response.data.errors.map(e => e.msg || e.message).join('\n');
        }

        if (err.response.status === 401 || err.response.status === 403) {
          handleLogout();
          return;
        }
      } else if (err.request) {
        errorMessage = '❌ Network error: Could not connect to server';
      } else {
        errorMessage = `❌ Error: ${err.message}`;
      }

      alert(errorMessage);
    } finally {
      setIsUpdating(false);
    }
  };

  const fetchSchoolData = async () => {
    try {
      const schoolId = storedUser.school?.id;
      const response = await api.get(`/school-info/${schoolId}`);
      console.log('School data updated:', response.data);
    } catch (err) {
      console.error('Failed to fetch school data:', err);
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh', background: theme.palette.background.default }}>
      <CssBaseline />

      {/* Top Bar */}
      <StyledAppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: isMobile ? theme.zIndex.appBar : theme.zIndex.drawer + 1,
          width: isMobile ? '100%' : `calc(100% - ${activeDrawerWidth}px)`,
          ml: isMobile ? 0 : `${activeDrawerWidth}px`,
          borderBottom: isMobile ? '0 !important' : undefined,
          '&::before, &::after': {
            display: 'none',
          },
        }}
      >
        <Toolbar sx={{
          justifyContent: 'space-between',
          minHeight: { xs: mobileTopBarHeight, sm: desktopTopBarHeight },
          px: isMobile ? 1 : 2,
          borderTop: '0 !important',
          boxShadow: 'none',
          borderBottom: isMobile
            ? `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.55 : 0.7)}`
            : 0
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Typography
              variant={isMobile ? "h6" : "h5"}
              fontWeight="700"
              sx={{
                letterSpacing: '-0.5px',
                fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.35rem', xl: '1.5rem' },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {schoolName}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
            {/* Global Actions (Moved from HomePage) */}
            <Tooltip title="Toggle theme">
              <IconButton size={isMobile ? 'small' : 'medium'} onClick={colorMode.toggleColorMode} sx={{ color: theme.palette.text.primary }}>
                <LightModeIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Search">
              <IconButton size={isMobile ? 'small' : 'medium'} onClick={() => setSearchDialogOpen(true)} sx={{ color: theme.palette.text.primary }}>
                <SearchIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton size={isMobile ? 'small' : 'medium'} onClick={handleBellClick} sx={{ color: theme.palette.text.primary }}>
              <Badge badgeContent={unreadCount} color="error">
                <NotificationsIcon fontSize="small" />
              </Badge>
            </IconButton>

            {/* School Location (Admin only) */}
            {isAdmin && !isMobile && (
              <Tooltip title="Update School Location">
                {isCompactDesktop ? (
                  <IconButton
                    size="medium"
                    onClick={() => setLocationModalOpen(true)}
                    sx={{ color: theme.palette.text.primary }}
                  >
                    <LocationOnIcon fontSize="small" />
                  </IconButton>
                ) : (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setLocationModalOpen(true)}
                    startIcon={<LocationOnIcon />}
                    sx={{
                      color: 'inherit',
                      borderColor: theme.palette.divider,
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        backgroundColor: alpha(theme.palette.primary.main, 0.04)
                      }
                    }}
                  >
                    School Location
                  </Button>
                )}
              </Tooltip>
            )}

            {/* User Menu with Profile Picture */}
            <Tooltip title="Account settings">
              <IconButton onClick={handleUserMenuOpen} sx={{ p: 0.5 }}>
                <Avatar
                  sx={{
                    width: { xs: 32, sm: 40 },
                    height: { xs: 32, sm: 40 },
                    bgcolor: profilePicture ? 'transparent' : theme.palette.primary.main,
                    fontWeight: 'bold',
                    fontSize: { xs: '0.9rem', sm: '1.1rem' },
                    border: profilePicture ? `2px solid ${theme.palette.primary.main}` : 'none'
                  }}
                  src={profilePicture}
                >
                  {!profilePicture && userInitial}
                </Avatar>
              </IconButton>
            </Tooltip>
          </Stack>
        </Toolbar>
      </StyledAppBar>

      {/* Sidebar Drawer */}
      <StyledDrawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? mobileDrawerOpen : true}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: isMobile ? mobileDrawerWidth : drawerWidth,
            ...(!isMobile && {
              width: activeDrawerWidth,
            }),
            // Fix: Ensure drawer is truly hidden when closed on mobile
            ...(isMobile && !mobileDrawerOpen && {
              transform: `translateX(-${mobileDrawerWidth}px)`,
              visibility: 'hidden',
            })
          },
        }}
      >

        <Box sx={{ overflowY: 'auto', py: 1, height: '100%' }}>
          {/* Academic Section */}
          <SectionContainer>
            <List sx={{ py: 0 }}>
              <ListSubheader
                sx={{
                  backgroundColor: 'transparent',
                  color: theme.palette.text.secondary,
                  fontWeight: 800,
                  fontSize: '0.85rem',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  lineHeight: '2',
                  textAlign: 'center',
                  px: 2,
                }}
              >
                📚 Academic
              </ListSubheader>
              {navigationItems.academic.map(renderSidebarItem)}
            </List>
          </SectionContainer>

          <Divider sx={{ my: 2, mx: 2 }} />

          {/* Operations Section */}
          <SectionContainer>
            <List sx={{ py: 0 }}>
              <ListSubheader
                sx={{
                  backgroundColor: 'transparent',
                  color: theme.palette.text.secondary,
                  fontWeight: 800,
                  fontSize: '0.85rem',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  lineHeight: '2',
                  textAlign: 'center',
                  px: 2,
                }}
              >
                ⚙️ Operations
              </ListSubheader>
              {navigationItems.operations
                .filter((item) => {
                  // Hide Feeding Fee Management for government schools
                  if (
                    item.path === '/dashboard/feeding-fees' &&
                    !canShowFeeding
                  ) {
                    return false;
                  }
                  return true;
                })
                .map(renderSidebarItem)}
            </List>
          </SectionContainer>

          <Divider sx={{ my: 2, mx: 2 }} />

          {/* Communication Section */}
          <SectionContainer>
            <List sx={{ py: 0 }}>
              <ListSubheader
                sx={{
                  backgroundColor: 'transparent',
                  color: theme.palette.text.secondary,
                  fontWeight: 800,
                  fontSize: '0.85rem',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  lineHeight: '2',
                  textAlign: 'center',
                  px: 2,
                }}
              >
                📢 Communication
              </ListSubheader>
              {navigationItems.communication.map(renderSidebarItem)}
            </List>
          </SectionContainer>

          {navigationItems.billing && (
            <>
              <Divider sx={{ my: 2, mx: 2 }} />
              <SectionContainer>
                <List sx={{ py: 0 }}>
                  <ListSubheader
                    sx={{
                      backgroundColor: 'transparent',
                      color: theme.palette.text.secondary,
                      fontWeight: 800,
                      fontSize: '0.85rem',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      lineHeight: '2',
                      textAlign: 'center',
                      px: 2,
                    }}
                  >
                    💳 CoreVista Billing
                  </ListSubheader>
                  {navigationItems.billing.map(renderSidebarItem)}
                </List>
              </SectionContainer>
            </>
          )}

          {navigationItems.payroll && (
            <>
              <Divider sx={{ my: 2, mx: 2 }} />
              <SectionContainer>
                <List sx={{ py: 0 }}>
                  <ListSubheader
                    sx={{
                      backgroundColor: 'transparent',
                      color: theme.palette.text.secondary,
                      fontWeight: 800,
                      fontSize: '0.85rem',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      lineHeight: '2',
                      textAlign: 'center',
                      px: 2,
                    }}
                  >
                    💰 Teacher Payroll
                  </ListSubheader>
                  {navigationItems.payroll.map(renderSidebarItem)}
                </List>
              </SectionContainer>
            </>
          )}

          {navigationItems.superAdmin && (
            <>
              <Divider sx={{ my: 2, mx: 2 }} />
              <SectionContainer>
                <List sx={{ py: 0 }}>
                  <ListSubheader
                    sx={{
                      backgroundColor: 'transparent',
                      color: theme.palette.text.secondary,
                      fontWeight: 800,
                      fontSize: '0.85rem',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      lineHeight: '2',
                      textAlign: 'center',
                      px: 2,
                    }}
                  >
                    🛡️ Super Admin
                  </ListSubheader>
                  {navigationItems.superAdmin.map(renderSidebarItem)}
                </List>
              </SectionContainer>
            </>
          )}

          {/* Mobile-only School Location Button */}
          {isMobile && isAdmin && (
            <>
              <Divider sx={{ my: 2, mx: 2 }} />
              <Box sx={{ p: 2 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => {
                    setLocationModalOpen(true);
                    setMobileDrawerOpen(false);
                  }}
                  startIcon={<LocationOnIcon />}
                  sx={{
                    color: sidebarText,
                    borderColor: alpha(sidebarActive, 0.35),
                    backgroundColor: alpha(sidebarActive, 0.08),
                    textTransform: 'none',
                    fontWeight: 700,
                    '&:hover': {
                      borderColor: alpha(sidebarActive, 0.55),
                      backgroundColor: alpha(sidebarActive, 0.14),
                    },
                    '& .MuiSvgIcon-root': {
                      color: sidebarActive,
                    },
                  }}
                >
                  School Location
                </Button>
              </Box>
            </>
          )}
        </Box>
      </StyledDrawer>

      {/* User Menu */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        PaperProps={{
          sx: {
            width: isMobile ? '100%' : 280,
            borderRadius: 3,
            mt: 1.5,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
          }
        }}
      >
        {/* Profile Header */}
        <Box sx={{ p: 2, textAlign: 'center', borderBottom: `1px solid ${theme.palette.divider}` }}>
          <StyledBadge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            variant="dot"
          >
            <Avatar
              sx={{
                width: 64,
                height: 64,
                mx: 'auto',
                mb: 1,
                bgcolor: profilePicture ? 'transparent' : theme.palette.primary.main,
                fontSize: '1.5rem',
                border: `3px solid ${theme.palette.primary.main}`
              }}
              src={profilePicture}
            >
              {!profilePicture && userInitial}
            </Avatar>
          </StyledBadge>
          <Typography variant="h6" fontWeight="600">{userName}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
            {storedUser.role}
          </Typography>
        </Box>

        {/* Profile Picture Actions */}
        <MenuItem
          component="label"
          disabled={isUploading}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <PhotoCameraIcon fontSize="small" />
          <Typography variant="body2">
            {isUploading ? 'Uploading...' : 'Update Photo'}
          </Typography>
          <VisuallyHiddenInput
            type="file"
            accept="image/*"
            onChange={handleProfilePictureUpload}
            disabled={isUploading}
          />
        </MenuItem>

        {profilePicture && (
          <MenuItem
            onClick={handleRemoveProfilePicture}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: 'error.main'
            }}
          >
            <DeleteIcon fontSize="small" />
            <Typography variant="body2">Remove Photo</Typography>
          </MenuItem>
        )}

        <Divider />

        {/* Other Menu Items */}
        <MenuItem onClick={handleUserMenuClose}>
          <EditIcon sx={{ mr: 2, fontSize: 20, color: 'primary.main' }} />
          <Typography variant="body2">Edit Profile</Typography>
        </MenuItem>

        <MenuItem onClick={handleUserMenuClose}>
          <SettingsIcon sx={{ mr: 2, fontSize: 20, color: 'primary.main' }} />
          <Typography variant="body2">Settings</Typography>
        </MenuItem>

        {/* ✅ Reset Password Menu Item */}
        <MenuItem
          onClick={() => {
            setResetModalOpen(true);
            handleUserMenuClose();
          }}
        >
          <SettingsIcon sx={{ mr: 2, fontSize: 20, color: 'primary.main' }} />
          <Typography variant="body2">Reset Password</Typography>
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
          <ExitToAppIcon sx={{ mr: 2, fontSize: 20 }} />
          <Typography variant="body2">Logout</Typography>
        </MenuItem>
      </Menu>

      {/* Main Content */}
      <MainContent sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <AppBarOffset />
        <Box sx={{ flexGrow: 1 }}>
          <InternalWorkspace><Outlet /></InternalWorkspace>
        </Box>
        <WhatsNewDialog platform="admin-dashboard" />

        {/* Global Footer */}
        <Box sx={{ py: 3, textAlign: 'center', mt: 'auto' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            © 2026 CoreVista Technologies. All rights reserved.
          </Typography>
        </Box>
      </MainContent>

      {/* ✅ Reset Password Modal */}
      <Dialog
        open={resetModalOpen}
        onClose={() => setResetModalOpen(false)}
        maxWidth="xs"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3, p: 2 } }}
      >
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 700 }}>
          Reset Password
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Old Password"
              type="password"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              fullWidth
            />
            <TextField
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
            />
            <TextField
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              fullWidth
            />

            {resetMessage && (
              <Alert
                severity={
                  resetMessage.startsWith('✅')
                    ? 'success'
                    : resetMessage.startsWith('⚠️')
                      ? 'warning'
                      : 'error'
                }
              >
                {resetMessage}
              </Alert>
            )}

            <Button
              variant="contained"
              onClick={handleResetPassword}
              disabled={isResetting}
            >
              {isResetting ? 'Updating...' : 'Update Password'}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* School Location Modal */}
      <Dialog
        open={locationModalOpen}
        onClose={() => {
          setLocationModalOpen(false);
          setActiveStep(0);
          setPolygonCoords('');
          setValidationError('');
          setCoordinates([]);
        }}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
      >
        <DialogTitle sx={{
          bgcolor: 'primary.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <LocationOnIcon />
          <Typography variant="h6" fontWeight="600">
            Update School Geofence
          </Typography>
        </DialogTitle>

        <Stepper
          activeStep={activeStep}
          sx={{ px: 3, pt: 2 }}
          orientation={isMobile ? 'vertical' : 'horizontal'}
        >
          {['Enter Coordinates', 'Review & Confirm'].map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <DialogContent sx={{ p: 3 }}>
          {activeStep === 0 ? (
            <Box>
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                Define your school boundaries so teachers can only clock in/out inside the school grounds.
              </Alert>

              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    How to get coordinates from Google Maps:
                  </Typography>
                  <ol>
                    <li>Open <a href="https://maps.google.com" target="_blank" rel="noopener noreferrer">Google Maps</a></li>
                    <li>Right-click on your school location and select "What's here?"</li>
                    <li>Copy the coordinates from the info box at the bottom</li>
                    <li>Repeat for all points around your school to form a closed shape</li>
                  </ol>
                </CardContent>
              </Card>

              <Grid container spacing={2} sx={{ mb: 2 }} direction={isMobile ? "column" : "row"}>
                <Grid size={{ xs: isMobile ? 12 : 5 }}>
                  <TextField
                    label="Latitude"
                    value={currentLat}
                    onChange={(e) => setCurrentLat(e.target.value)}
                    fullWidth
                    InputProps={{
                      endAdornment: <InputAdornment position="end">°</InputAdornment>,
                    }}
                    placeholder="e.g., 5.6037"
                  />
                </Grid>
                <Grid size={{ xs: isMobile ? 12 : 5 }}>
                  <TextField
                    label="Longitude"
                    value={currentLng}
                    onChange={(e) => setCurrentLng(e.target.value)}
                    fullWidth
                    InputProps={{
                      endAdornment: <InputAdornment position="end">°</InputAdornment>,
                    }}
                    placeholder="e.g., -0.2100"
                  />
                </Grid>
                <Grid size={{ xs: isMobile ? 12 : 2 }}>
                  <Button
                    variant="contained"
                    onClick={addCoordinate}
                    fullWidth
                    sx={{ height: isMobile ? '48px' : '100%' }}
                    startIcon={<AddIcon />}
                  >
                    Add
                  </Button>
                </Grid>
              </Grid>

              {validationError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {validationError}
                </Alert>
              )}

              {coordinates.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Added Coordinates (Longitude, Latitude):
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {coordinates.map((coord, index) => (
                      <Chip
                        key={index}
                        label={`${coord[0].toFixed(4)}, ${coord[1].toFixed(4)}`}
                        onDelete={() => removeCoordinate(index)}
                        color="primary"
                        variant="outlined"
                        deleteIcon={<DeleteIcon />}
                        size={isMobile ? "small" : "medium"}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              <Typography variant="subtitle2" gutterBottom>
                Generated JSON (automatically updates):
              </Typography>

              <TextField
                value={polygonCoords}
                onChange={handleCoordinatesChange}
                multiline
                minRows={6}
                fullWidth
                variant="outlined"
                sx={{ fontFamily: 'monospace' }}
                error={!!validationError}
                helperText={validationError || "First and last coordinates will automatically match to form a closed polygon"}
              />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {isValidJSON ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', color: 'success.main' }}>
                      <CheckCircleIcon sx={{ mr: 0.5, fontSize: 20 }} />
                      <Typography variant="body2">Valid JSON format</Typography>
                    </Box>
                  ) : validationError ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', color: 'error.main' }}>
                      <ErrorIcon sx={{ mr: 0.5, fontSize: 20 }} />
                      <Typography variant="body2">Invalid format</Typography>
                    </Box>
                  ) : null}
                </Box>

                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={!isValidJSON}
                >
                  Next
                </Button>
              </Box>
            </Box>
          ) : (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Please review the coordinates before saving. Incorrect coordinates may affect attendance tracking.
              </Alert>

              <Typography variant="h6" gutterBottom>
                Coordinates to be saved:
              </Typography>

              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <pre style={{
                    fontSize: 12,
                    background: '#f5f5f5',
                    padding: 8,
                    borderRadius: 4,
                    overflowX: 'auto',
                    maxHeight: '200px'
                  }}>
                    {polygonCoords}
                  </pre>
                </CardContent>
              </Card>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button onClick={handleBack}>
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={handleUpdateLocation}
                  disabled={isUpdating}
                  startIcon={isUpdating ? <CircularProgress size={20} /> : null}
                >
                  {isUpdating ? 'Updating...' : 'Save Location'}
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Global Actions Modals */}
      <Dialog
        open={searchDialogOpen}
        onClose={() => {
          setSearchDialogOpen(false);
          setSearch('');
          setSearchResults(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            maxHeight: '80vh',
            backgroundImage: 'none',
            boxShadow: theme.shadows[20]
          }
        }}
        TransitionProps={{
          onEntered: (node) => {
            node.querySelector('input')?.focus(); // Manual focus if autoFocus fails
          }
        }}
      >
        <Box sx={{ p: 0 }}>
          {/* Header & Input Area */}
          <Box sx={{
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            bgcolor: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: 'blur(8px)',
            position: 'sticky',
            top: 0,
            zIndex: 10
          }}>
            <SearchIcon color="action" />
            <TextField
              fullWidth
              variant="standard"
              placeholder="Search students, teachers, classes..."
              value={search}
              autoFocus
              onChange={(e) => {
                const query = e.target.value;
                setSearch(query);
                if (query.trim().length > 1) {
                  handleSearch(query);
                } else {
                  setSearchResults(null);
                }
              }}
              InputProps={{
                disableUnderline: true,
                sx: { fontSize: '1.2rem', fontWeight: 500 }
              }}
            />
            {loadingSearch && <CircularProgress size={24} thickness={4} />}
            <IconButton
              size="small"
              onClick={() => {
                setSearchDialogOpen(false);
                setSearch('');
              }}
              sx={{ bgcolor: alpha(theme.palette.action.active, 0.05) }}
            >
              <Box component="span" sx={{ fontSize: '1.2rem', lineHeight: 1 }}>&times;</Box>
            </IconButton>
          </Box>

          {/* Results Area */}
          <DialogContent sx={{ p: 0, minHeight: 300, bgcolor: theme.palette.background.default }}>
            {!search && (
              <Box sx={{
                height: 300,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
                gap: 1
              }}>
                <SearchIcon sx={{ fontSize: 48, opacity: 0.2 }} />
                <Typography variant="body2">Start typing to search...</Typography>
              </Box>
            )}

            {search && !searchResults && !loadingSearch && (
              <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                <Typography>Type at least 2 characters to search</Typography>
              </Box>
            )}

            {searchResults && (
              <Box>
                {Object.values(searchResults).every(arr => !arr || arr.length === 0) ? (
                  <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                    <Typography>No results found for "{search}"</Typography>
                  </Box>
                ) : (
                  <List sx={{ pt: 0 }}>
                    {/* Students */}
                    {searchResults.students?.length > 0 && (
                      <>
                        <ListSubheader sx={{ bgcolor: 'background.paper', fontWeight: 'bold' }}>
                          Students
                        </ListSubheader>
                        {searchResults.students.map((student) => (
                          <ListItem
                            key={student._id}
                            component="div"
                            onClick={() => {
                              navigate(`/dashboard/students?search=${student.admissionNumber}`);
                              setSearchDialogOpen(false);
                            }}
                            sx={{ '&:hover': { bgcolor: 'action.hover', cursor: 'pointer' } }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                              <StudentAvatar student={student}>{student.name?.charAt(0)}</StudentAvatar>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle2">{student.name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {student.admissionNumber} • {student.class?.name || 'No Class'}
                                </Typography>
                              </Box>
                            </Box>
                          </ListItem>
                        ))}
                      </>
                    )}

                    {/* Teachers */}
                    {searchResults.teachers?.length > 0 && (
                      <>
                        <ListSubheader sx={{ bgcolor: 'background.paper', fontWeight: 'bold' }}>
                          Teachers
                        </ListSubheader>
                        {searchResults.teachers.map((teacher) => (
                          <ListItem
                            key={teacher._id}
                            component="div"
                            onClick={() => {
                              navigate(`/dashboard/teachers?search=${teacher._id}`);
                              setSearchDialogOpen(false);
                            }}
                            sx={{ '&:hover': { bgcolor: 'action.hover', cursor: 'pointer' } }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                              <Avatar src={teacher.user?.profilePicture}>{teacher.name?.charAt(0)}</Avatar>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle2">{teacher.name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {teacher.subject || 'Teacher'}
                                </Typography>
                              </Box>
                            </Box>
                          </ListItem>
                        ))}
                      </>
                    )}

                    {/* Classes */}
                    {searchResults.classes?.length > 0 && (
                      <>
                        <ListSubheader sx={{ bgcolor: 'background.paper', fontWeight: 'bold' }}>
                          Classes
                        </ListSubheader>
                        {searchResults.classes.map((cls) => (
                          <ListItem
                            key={cls._id}
                            component="div"
                            onClick={() => {
                              navigate(`/dashboard/classes?search=${cls._id}`);
                              setSearchDialogOpen(false);
                            }}
                            sx={{ '&:hover': { bgcolor: 'action.hover', cursor: 'pointer' } }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                              <Avatar><ClassIcon /></Avatar>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle2">{cls.name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Class
                                </Typography>
                              </Box>
                            </Box>
                          </ListItem>
                        ))}
                      </>
                    )}
                  </List>
                )}
              </Box>
            )}
          </DialogContent>
        </Box>
      </Dialog>

      <Menu
        anchorEl={anchorElNotifications}
        open={Boolean(anchorElNotifications)}
        onClose={() => setAnchorElNotifications(null)}
        PaperProps={{
          sx: {
            width: 320,
            maxHeight: 400,
            mt: 1.5,
            borderRadius: 3,
            boxShadow: theme.shadows[10],
            '&::-webkit-scrollbar': { display: 'none' },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Typography variant="subtitle1" sx={{ px: 2, pt: 1 }}>Notifications</Typography>
        <Divider sx={{ my: 1 }} />
        {notifications.length === 0 ? (
          <MenuItem disabled>No new notifications</MenuItem>
        ) : (
          notifications.slice(0, 5).map((note, i) => (
            <MenuItem key={note._id || i} onClick={() => handleNotificationClick(note)}>
              <Box display="flex" flexDirection="column" gap={0.5}>
                <Typography variant="body2" fontWeight={!note.isRead ? 600 : 400}>
                  {typeof note.title === 'string' ? note.title.replace(/Recovered/gi, 'Arrears') : (note.title || 'Notification')}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 280 }}>
                  {typeof note.message === 'string' ? note.message.replace(/recovered/i, 'arrears') : note.message}
                </Typography>
              </Box>
            </MenuItem>
          ))
        )}
        <Divider sx={{ my: 1 }} />
        <MenuItem onClick={() => {
          setAnchorElNotifications(null);
          navigate('/dashboard/notifications');
        }} sx={{ justifyContent: 'center', color: theme.palette.primary.main }}>
          View All Notifications
        </MenuItem>
      </Menu>

    </Box>
  );
}
